import { useState, useEffect, useRef } from 'react'
import { Button, Chip, Avatar, Alert, Input, Textarea, EmptyState } from '@/components/ui'
import {
  CheckCircle2, XCircle, PauseCircle, AlertTriangle, Clock, Receipt,
  Loader2, History, Image as ImageIcon,
} from 'lucide-react'
import { fmtMoney, cn } from '@/lib/utils'
import {
  useComprobantesPendientes,
  useSignedUrlComprobante,
  useActualizarComprobante,
  useResolverComprobante,
  useAuditComprobante,
  derivarMotivo,
  calcularEspera,
  type ComprobanteRow,
  type CampoEditable,
  type Decision,
} from '@/lib/api/comprobantes'

const DEBOUNCE_MS = 600

export function Pendientes() {
  const { data: pendientes = [], isLoading, error, refetch } = useComprobantesPendientes()
  const [selId, setSelId] = useState<string | null>(null)

  // Auto-seleccionar el primero cuando llegan los datos
  useEffect(() => {
    if (!selId && pendientes.length > 0) setSelId(pendientes[0].id)
    if (selId && !pendientes.find(p => p.id === selId) && pendientes.length > 0) {
      setSelId(pendientes[0].id)
    }
  }, [pendientes, selId])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        <span className="ml-2 text-slate-500">Cargando pendientes…</span>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="danger" title="Error al cargar comprobantes">
        {(error as Error).message}
        <Button variant="outline" size="sm" className="mt-2" onClick={() => refetch()}>
          Reintentar
        </Button>
      </Alert>
    )
  }

  if (pendientes.length === 0) {
    return (
      <EmptyState
        icon={<CheckCircle2 className="w-7 h-7 text-emerald-500" />}
        title="No hay comprobantes pendientes"
        description="Todos los comprobantes recibidos fueron validados automáticamente o ya se resolvieron."
        action={
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            Refrescar
          </Button>
        }
      />
    )
  }

  const sel = pendientes.find(p => p.id === selId) ?? pendientes[0]

  return (
    <div className="grid grid-cols-12 gap-5 min-h-[700px]">
      <ColumnaLista pendientes={pendientes} selId={sel.id} onSelect={setSelId} />
      <ColumnaPreview comprobante={sel} />
      <ColumnaEdicion comprobante={sel} />
    </div>
  )
}

// ─── Columna 1: Lista de pendientes ────────────────────────────────────────

function ColumnaLista({
  pendientes,
  selId,
  onSelect,
}: {
  pendientes: ComprobanteRow[]
  selId: string
  onSelect: (id: string) => void
}) {
  return (
    <div className="col-span-3 bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col">
      <header className="px-4 py-3 border-b border-slate-200">
        <h2 className="font-display text-base text-navy-700">Pendientes</h2>
        <p className="text-[11px] text-slate-500">{pendientes.length} esperando revisión</p>
      </header>
      <ul className="flex-1 overflow-y-auto divide-y divide-slate-100">
        {pendientes.map(p => {
          const active = selId === p.id
          const espera = calcularEspera(p.creado_en)
          const motivo = derivarMotivo(p)
          return (
            <li
              key={p.id}
              onClick={() => onSelect(p.id)}
              className={cn(
                'p-3 cursor-pointer transition',
                active ? 'bg-rose-50 border-l-4 border-rose-500' : 'hover:bg-slate-50',
                espera.urgente && !active && 'bg-amber-50/50'
              )}
            >
              <div className="flex items-start gap-2">
                <div className="w-12 h-12 bg-slate-100 rounded-md flex items-center justify-center shrink-0">
                  <Receipt className="w-5 h-5 text-slate-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-navy-700 text-sm truncate">
                    {p.cliente_nombre ?? p.cedula}
                  </div>
                  <div className="text-xs text-slate-500 font-mono">{p.cedula}</div>
                  <div className="text-sm font-mono font-semibold text-navy-700 mt-0.5">
                    {p.monto_extraido ? fmtMoney(p.monto_extraido) : '—'}
                  </div>
                  <Chip variant={motivo.color} className="text-[10px] mt-1">
                    {motivo.chip}
                  </Chip>
                  <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                    <Clock className="w-2.5 h-2.5" />
                    {espera.horas > 0 ? `${espera.horas}h ${espera.minutos}min` : `${espera.minutos} min`}
                    {espera.urgente && <span className="text-rose-600 font-semibold ml-1">URGENTE</span>}
                  </div>
                </div>
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

// ─── Columna 2: Preview de la imagen ────────────────────────────────────────

function ColumnaPreview({ comprobante }: { comprobante: ComprobanteRow }) {
  const { data: signedUrl, isLoading: urlLoading } = useSignedUrlComprobante(comprobante.id)

  return (
    <div className="col-span-5 bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col">
      <header className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
          Comprobante{comprobante.numero_referencia && <span className="ml-1 font-mono normal-case text-slate-500">#{comprobante.numero_referencia}</span>}
        </div>
        <div className="text-[11px] text-slate-500">
          🤖 {comprobante.modelo_usado ?? 'GPT-4.1 vision'} ·
          Confianza{' '}
          <span className={cn(
            'font-mono font-semibold',
            (comprobante.confianza_ia ?? 0) >= 0.85 ? 'text-emerald-600'
              : (comprobante.confianza_ia ?? 0) >= 0.60 ? 'text-amber-600' : 'text-rose-600'
          )}>
            {Math.round((comprobante.confianza_ia ?? 0) * 100)}%
          </span>
        </div>
      </header>
      <div className="flex-1 bg-slate-100 p-6 flex items-center justify-center overflow-auto">
        {urlLoading ? (
          <div className="flex items-center gap-2 text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin" /> Cargando imagen…
          </div>
        ) : signedUrl ? (
          <img
            src={signedUrl}
            alt="Comprobante"
            className="max-w-full max-h-[600px] rounded-lg shadow-xl bg-white"
            onError={() => console.error('No se pudo cargar la imagen signed URL')}
          />
        ) : (
          <div className="text-center text-slate-500">
            <ImageIcon className="w-12 h-12 mx-auto mb-2 text-slate-300" />
            <p className="text-sm">Sin imagen disponible</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Columna 3: Edición inline + resolución ────────────────────────────────

function ColumnaEdicion({ comprobante }: { comprobante: ComprobanteRow }) {
  const motivo = derivarMotivo(comprobante)
  const [modal, setModal] = useState<Decision | null>(null)

  // Match_bd info visible aunque match_razon sea null
  const matchBd = comprobante.match_bd === true

  return (
    <div className="col-span-4 flex flex-col gap-4">
      <Alert
        variant={motivo.color === 'amber' ? 'warning' : 'danger'}
        title={<span className="font-mono text-xs">{motivo.key}</span>}
      >
        <p className="text-xs mt-1">{motivo.descripcion}</p>
      </Alert>

      <section className="bg-white border border-slate-200 rounded-xl p-4">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Avatar name={comprobante.cliente_nombre ?? comprobante.cedula} size="sm" /> Cliente
        </h3>
        <dl className="space-y-1.5 text-xs">
          <Row label="Nombre" value={<span className="text-navy-700 font-semibold">{comprobante.cliente_nombre ?? '— sin vincular —'}</span>} />
          <Row label="Cédula" value={<span className="font-mono">{comprobante.cedula}</span>} />
          <Row label="WhatsApp" value={<span className="font-mono">{comprobante.numero_whatsapp}</span>} />
          {comprobante.saldo_vencido_bd !== null && (
            <Row
              label="Saldo vencido BD"
              value={<span className="font-mono font-semibold text-rose-600">{fmtMoney(comprobante.saldo_vencido_bd)}</span>}
            />
          )}
          {comprobante.cartera_bd && (
            <Row label="Cartera" value={<span className="font-mono text-slate-700">{comprobante.cartera_bd}</span>} />
          )}
          <Row
            label="Match BD"
            value={
              matchBd
                ? <Chip variant="emerald">✓ Cruce OK</Chip>
                : <Chip variant="rose">✗ Sin match</Chip>
            }
          />
        </dl>
      </section>

      <CamposEditablesPanel comprobante={comprobante} />

      <section className="bg-white border border-slate-200 rounded-xl p-4">
        <AccionesResolver comprobante={comprobante} onAbrirModal={setModal} />
      </section>

      {modal && (
        <ModalResolver
          comprobante={comprobante}
          decision={modal}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  )
}

// ─── Panel de campos editables con debounce ────────────────────────────────

function CamposEditablesPanel({ comprobante }: { comprobante: ComprobanteRow }) {
  const actualizar = useActualizarComprobante()
  const { data: audit = [] } = useAuditComprobante(comprobante.id)

  // Estado local optimista para los inputs
  const [local, setLocal] = useState({
    monto_extraido: comprobante.monto_extraido?.toString() ?? '',
    fecha_extraida: comprobante.fecha_extraida ?? '',
    banco_extraido: comprobante.banco_extraido ?? '',
    ultimos_4_extraidos: comprobante.ultimos_4_extraidos ?? '',
    titular_extraido: comprobante.titular_extraido ?? '',
    numero_referencia: comprobante.numero_referencia ?? '',
    tipo_transaccion: comprobante.tipo_transaccion ?? '',
  })

  // Resetear cuando cambia de comprobante
  useEffect(() => {
    setLocal({
      monto_extraido: comprobante.monto_extraido?.toString() ?? '',
      fecha_extraida: comprobante.fecha_extraida ?? '',
      banco_extraido: comprobante.banco_extraido ?? '',
      ultimos_4_extraidos: comprobante.ultimos_4_extraidos ?? '',
      titular_extraido: comprobante.titular_extraido ?? '',
      numero_referencia: comprobante.numero_referencia ?? '',
      tipo_transaccion: comprobante.tipo_transaccion ?? '',
    })
  }, [comprobante.id])

  // Debounce de updates
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({})

  const update = (campo: CampoEditable, raw: string) => {
    setLocal(prev => ({ ...prev, [campo]: raw }))
    if (timers.current[campo]) clearTimeout(timers.current[campo])
    timers.current[campo] = setTimeout(() => {
      // Normalizar: trim + null si vacío
      const valor = raw.trim() === '' ? null : raw.trim()
      const original = (comprobante as unknown as Record<string, unknown>)[campo]
      const originalStr = original == null ? null : String(original)
      if (valor === originalStr) return // Sin cambios netos
      actualizar.mutate({
        id: comprobante.id,
        cambios: { [campo]: valor } as Partial<Record<CampoEditable, string | number | null>>,
      })
    }, DEBOUNCE_MS)
  }

  const titular = (local.titular_extraido ?? '').toLowerCase()
  const esPointTitular =
    titular.includes('point') ||
    titular.includes('compubussines') ||
    titular.includes('supermercado de computadoras')

  const fechaMesActual = (() => {
    if (!local.fecha_extraida) return true
    const f = new Date(local.fecha_extraida)
    const h = new Date()
    return f.getUTCFullYear() === h.getUTCFullYear() && f.getUTCMonth() === h.getUTCMonth()
  })()

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 flex-1 overflow-y-auto">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
          Campos extraídos por IA
        </h3>
        <div className="flex items-center gap-2 text-[10px] text-slate-500">
          {actualizar.isPending && (
            <span className="flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Guardando…
            </span>
          )}
          {!actualizar.isPending && actualizar.isSuccess && (
            <span className="text-emerald-600 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Guardado
            </span>
          )}
          {audit.length > 0 && (
            <span className="flex items-center gap-1 text-slate-500">
              <History className="w-3 h-3" /> {audit.length} edición{audit.length !== 1 ? 'es' : ''}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <Input
          label={
            <span className="flex items-center gap-1">
              Monto <ValidationChip ok={!!local.monto_extraido && Number(local.monto_extraido) > 0} />
            </span>
          }
          type="number"
          step="0.01"
          value={local.monto_extraido}
          onChange={e => update('monto_extraido', e.target.value)}
        />
        <Input
          label={
            <span className="flex items-center gap-1">
              Fecha{' '}
              {!fechaMesActual && local.fecha_extraida && (
                <span className="text-[10px] bg-rose-100 text-rose-700 px-1 rounded">FUERA DEL MES</span>
              )}
            </span>
          }
          type="date"
          value={local.fecha_extraida}
          onChange={e => update('fecha_extraida', e.target.value)}
          error={!fechaMesActual && local.fecha_extraida ? 'Fecha de otro mes. Pedí el correcto si corresponde.' : undefined}
        />
        <Input
          label={
            <span className="flex items-center gap-1">
              Beneficiario{' '}
              {local.titular_extraido && (
                esPointTitular
                  ? <span className="text-[10px] text-emerald-600 font-semibold">✓ Point oficial</span>
                  : <span className="text-[10px] bg-rose-100 text-rose-700 px-1 rounded">NO Point</span>
              )}
            </span>
          }
          value={local.titular_extraido}
          onChange={e => update('titular_extraido', e.target.value)}
        />
        <div className="grid grid-cols-2 gap-2">
          <Input
            label="Últimos 4"
            value={local.ultimos_4_extraidos}
            maxLength={4}
            onChange={e => update('ultimos_4_extraidos', e.target.value)}
            className="font-mono"
          />
          <Input
            label="Banco"
            value={local.banco_extraido}
            onChange={e => update('banco_extraido', e.target.value)}
          />
        </div>
        <Input
          label="N° referencia"
          value={local.numero_referencia}
          onChange={e => update('numero_referencia', e.target.value)}
          className="font-mono"
        />
        <Input
          label="Tipo transacción"
          value={local.tipo_transaccion}
          onChange={e => update('tipo_transaccion', e.target.value)}
          placeholder="transferencia / depósito / pago..."
        />
      </div>

      {audit.length > 0 && (
        <details className="mt-4 pt-3 border-t border-slate-100">
          <summary className="text-[11px] text-slate-500 cursor-pointer hover:text-navy-700">
            Ver historial de ediciones ({audit.length})
          </summary>
          <ul className="mt-2 space-y-1.5">
            {audit.slice(0, 10).map(a => (
              <li key={a.id} className="text-[11px] bg-slate-50 rounded px-2 py-1.5">
                <div className="font-mono text-navy-700 font-semibold">{a.campo}</div>
                <div className="text-slate-500 mt-0.5">
                  <span className="line-through">{a.valor_anterior ?? '∅'}</span>
                  {' → '}
                  <span className="text-emerald-700 font-semibold">{a.valor_nuevo ?? '∅'}</span>
                </div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  {new Date(a.editado_at).toLocaleString('es-EC')}
                </div>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}

function ValidationChip({ ok }: { ok: boolean }) {
  return ok ? (
    <span className="text-[10px] text-emerald-600 font-semibold">✓ OK</span>
  ) : (
    <span className="text-[10px] bg-amber-100 text-amber-700 px-1 rounded">revisar</span>
  )
}

// ─── Acciones Aprobar/Rechazar/Pedir ──────────────────────────────────────

function AccionesResolver({
  comprobante,
  onAbrirModal,
}: {
  comprobante: ComprobanteRow
  onAbrirModal: (d: Decision) => void
}) {
  const saldo = comprobante.saldo_vencido_bd ?? 0
  const monto = comprobante.monto_extraido ?? 0
  const restaria = Math.max(0, saldo - monto)

  return (
    <>
      {comprobante.saldo_vencido_bd !== null && (
        <div className="bg-slate-50 rounded-lg p-3 mb-3">
          <h4 className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
            Impacto en el saldo
          </h4>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-[10px] uppercase text-slate-500">Comprob.</div>
              <div className="font-mono font-semibold text-navy-700">{fmtMoney(monto)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-500">Saldo</div>
              <div className="font-mono font-semibold text-rose-600">{fmtMoney(saldo)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-slate-500">Restaría</div>
              <div className="font-mono font-semibold text-emerald-600">{fmtMoney(restaria)}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2">
        <Button variant="rose" size="sm" onClick={() => onAbrirModal('rechazar')}>
          <XCircle className="w-3.5 h-3.5" /> Rechazar
        </Button>
        <Button variant="outline" size="sm" className="border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => onAbrirModal('pedir_correcto')}>
          <PauseCircle className="w-3.5 h-3.5" /> Pedir correcto
        </Button>
        <Button variant="emerald" size="sm" onClick={() => onAbrirModal('aprobar')}>
          <CheckCircle2 className="w-3.5 h-3.5" /> Aprobar
        </Button>
      </div>
    </>
  )
}

// ─── Modal de confirmación ────────────────────────────────────────────────

function ModalResolver({
  comprobante,
  decision,
  onClose,
}: {
  comprobante: ComprobanteRow
  decision: Decision
  onClose: () => void
}) {
  const resolver = useResolverComprobante()
  const [nota, setNota] = useState('')
  const notaObligatoria = decision !== 'aprobar'
  const puedeConfirmar = !notaObligatoria || nota.trim().length >= 3

  const titulos: Record<Decision, { title: string; color: string; icon: typeof CheckCircle2 }> = {
    aprobar: { title: 'Aprobar comprobante', color: 'emerald', icon: CheckCircle2 },
    rechazar: { title: 'Rechazar comprobante', color: 'rose', icon: XCircle },
    pedir_correcto: { title: 'Pedir correcto al cliente', color: 'amber', icon: PauseCircle },
  }
  const { title, color, icon: Icon } = titulos[decision]

  const confirmar = async () => {
    try {
      const resp = await resolver.mutateAsync({
        id: comprobante.id,
        decision,
        motivo: nota.trim() || null,
      })
      if (resp.ok) onClose()
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-5"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className={cn(
            'w-10 h-10 rounded-lg flex items-center justify-center',
            color === 'emerald' && 'bg-emerald-100 text-emerald-700',
            color === 'rose' && 'bg-rose-100 text-rose-700',
            color === 'amber' && 'bg-amber-100 text-amber-700'
          )}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display text-lg text-navy-700">{title}</h3>
            <p className="text-xs text-slate-500">
              {comprobante.cliente_nombre ?? comprobante.cedula} ·{' '}
              {comprobante.monto_extraido ? fmtMoney(comprobante.monto_extraido) : '—'}
            </p>
          </div>
        </div>

        {decision === 'pedir_correcto' && (
          <Alert variant="warning" className="mb-3">
            El cliente recibirá un mensaje pidiendo que mande otra foto del comprobante.
          </Alert>
        )}

        <Textarea
          label={<span>Motivo {notaObligatoria && <span className="text-rose-600">*</span>}</span>}
          placeholder={notaObligatoria ? 'Explicá por qué (visible en audit)' : 'Opcional'}
          rows={3}
          value={nota}
          onChange={e => setNota(e.target.value)}
          hint={notaObligatoria ? 'Mínimo 3 caracteres' : undefined}
        />

        {resolver.isError && (
          <Alert variant="danger" className="mt-3">
            {(resolver.error as Error).message}
          </Alert>
        )}

        <div className="flex items-center justify-end gap-2 mt-5">
          <Button variant="outline" size="sm" onClick={onClose} disabled={resolver.isPending}>
            Cancelar
          </Button>
          <Button
            variant={color === 'emerald' ? 'emerald' : color === 'rose' ? 'rose' : 'amber'}
            size="sm"
            onClick={confirmar}
            disabled={!puedeConfirmar || resolver.isPending}
          >
            {resolver.isPending ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Procesando…</>
            ) : (
              <><Icon className="w-3.5 h-3.5" /> Confirmar</>
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start gap-3">
      <dt className="text-slate-500 shrink-0">{label}</dt>
      <dd className="text-right min-w-0">{value}</dd>
    </div>
  )
}

// Export por nombre + default también por si alguien lo importa de otra manera
export default Pendientes
